"use client";

import { clearYzsaCache } from "@yardzen/next-api-util/yzsa-utils";
import Cookies from "js-cookie";
import { useEffect } from "react";
import { generateRandomHex } from "../utils/random";

const YZSA_COOKIE_NAME = "yzsa";
const YZSA_LOCALSTORAGE_KEY = "yzsa";

/**
 * Client-side component that manages yzsa ID synchronization between localStorage and cookies.
 * This component should be included in the app layout to run on every page load.
 *
 * How it works:
 * 1. First checks localStorage for existing yzsa value
 * 2. If localStorage has value, syncs it to cookie via middleware
 * 3. If no localStorage value, uses cookie value and saves to localStorage
 * 4. If neither exist, generates new value and saves to localStorage
 */
export function YzsaSync() {
  useEffect(() => {
    const syncYzsa = () => {
      try {
        // Step 1: Check localStorage first
        const localStorageValue = localStorage.getItem(YZSA_LOCALSTORAGE_KEY);
        const cookieValue = Cookies.get(YZSA_COOKIE_NAME);

        if (localStorageValue && /^[a-f0-9]{32}$/i.test(localStorageValue)) {
          // Step 2: localStorage has valid value - sync to cookie if different
          if (localStorageValue !== cookieValue) {
            console.info(
              "[YzsaSync] Syncing localStorage yzsa value to cookie",
            );

            // Set the cookie directly using js-cookie
            Cookies.set(YZSA_COOKIE_NAME, localStorageValue, {
              expires: 365, // 1 year
              path: "/",
              sameSite: "lax",
            });

            // Clear cache so getYzsaId() picks up the new value
            clearYzsaCache();
          }
        } else if (cookieValue && /^[a-f0-9]{32}$/i.test(cookieValue)) {
          // Step 3: No valid localStorage value but cookie exists - save to localStorage
          localStorage.setItem(YZSA_LOCALSTORAGE_KEY, cookieValue);
          console.info("[YzsaSync] Synced cookie yzsa value to localStorage");

          // Clear cache so getYzsaId() picks up the new value
          clearYzsaCache();
        } else {
          // Step 4: Neither exist - generate new value and save to localStorage
          // The middleware will generate and set the cookie on the next page load
          const newYzsa = generateRandomHex(16);
          localStorage.setItem(YZSA_LOCALSTORAGE_KEY, newYzsa);
          console.info("[YzsaSync] Generated new yzsa value");

          // Clear cache so getYzsaId() picks up the new value
          clearYzsaCache();
        }
      } catch (error) {
        console.warn("[YzsaSync] Failed to sync yzsa values:", error);
      }
    };

    // Sync immediately on mount
    syncYzsa();

    // Also sync when the window gains focus (in case values changed in another tab)
    const handleFocus = () => {
      syncYzsa();
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  // This component renders nothing, it's just for the side effect
  return null;
}
